/**
 * Copy da chamada de vídeo por tipo de uso:
 * - support: atendimento ao cliente (chat / lobby do canal)
 * - team: comunicação interna (conversa da equipe)
 * - agenda: compromisso da agenda
 * - virtual_office: escritório virtual (presença; ponto só se o regime legal exigir)
 */

import { virtualOfficeCopy } from "../VirtualOffice/attendanceCopy";
import { floorPresenceCopy } from "./voFloorLiving";

export const CALL_CONTEXT_SUPPORT = "support";
export const CALL_CONTEXT_TEAM = "team";
export const CALL_CONTEXT_AGENDA = "agenda";
export const CALL_CONTEXT_VIRTUAL_OFFICE = "virtual_office";
/** Conversa privada iniciada a partir do escritório (não é o floor; sem ponto). */
export const CALL_CONTEXT_VO_PRIVATE = "vo_private";
/** Sala interna (café / reunião) do escritório — pública ou privada. */
export const CALL_CONTEXT_VO_HUDDLE = "vo_huddle";

export function resolveCallContext({
  apiContext = null,
  searchParam = null,
  fromLobby = false,
  teamSpaceId = null,
} = {}) {
  const raw = String(apiContext || searchParam || "")
    .trim()
    .toLowerCase();
  if (
    raw === "virtual_office" ||
    raw === "virtual-office" ||
    raw === "escritorio_virtual" ||
    raw === "escritorio-virtual" ||
    raw === "escritorio"
  ) {
    return CALL_CONTEXT_VIRTUAL_OFFICE;
  }
  if (raw === "vo_private" || raw === "vo-private" || raw === "escritorio_privado") {
    return CALL_CONTEXT_VO_PRIVATE;
  }
  if (raw === "vo_huddle" || raw === "vo-huddle" || raw === "escritorio_sala") {
    return CALL_CONTEXT_VO_HUDDLE;
  }
  if (raw === "team" || raw === "internal" || raw === "equipe") {
    return CALL_CONTEXT_TEAM;
  }
  if (raw === "agenda" || raw === "calendar" || raw === "reuniao" || raw === "reunião") {
    return CALL_CONTEXT_AGENDA;
  }
  const spaceId = Number(teamSpaceId);
  if (Number.isFinite(spaceId) && spaceId > 0) return CALL_CONTEXT_TEAM;
  if (fromLobby) return CALL_CONTEXT_SUPPORT;
  if (raw === "support" || raw === "lead" || raw === "customer" || raw === "atendimento") {
    return CALL_CONTEXT_SUPPORT;
  }
  return CALL_CONTEXT_SUPPORT;
}

export function isTeamCallContext(callContext) {
  return callContext === CALL_CONTEXT_TEAM;
}

export function isAgendaCallContext(callContext) {
  return callContext === CALL_CONTEXT_AGENDA;
}

export function isVirtualOfficeCallContext(callContext) {
  return callContext === CALL_CONTEXT_VIRTUAL_OFFICE;
}

export function isVoPrivateCallContext(callContext) {
  return callContext === CALL_CONTEXT_VO_PRIVATE;
}

export function isVoHuddleCallContext(callContext) {
  return callContext === CALL_CONTEXT_VO_HUDDLE;
}

export function isVoSideRoomCallContext(callContext) {
  return isVoPrivateCallContext(callContext) || isVoHuddleCallContext(callContext);
}

export function isAnyVirtualOfficeVideoContext(callContext) {
  return isVirtualOfficeCallContext(callContext) || isVoSideRoomCallContext(callContext);
}

export function roomLabelForContext(channelName, { callContext, fromLobby } = {}) {
  const name = String(channelName || "").trim();
  if (name) return name;
  if (isVoHuddleCallContext(callContext)) return "Sala do escritório";
  if (isVoPrivateCallContext(callContext)) return "Conversa privada";
  if (isVirtualOfficeCallContext(callContext)) return "Escritório Virtual";
  if (isTeamCallContext(callContext)) return "Chamada da equipe";
  if (isAgendaCallContext(callContext)) return "Reunião";
  if (fromLobby) return "Atendimento por vídeo";
  return "Chamada de vídeo";
}

/**
 * Copy pós-chamada: perspectiva local + o que encerrou a sessão.
 */
export function endedCallCopy({
  callContext,
  joinRole,
  endCause,
  fromLobbyEntry,
  remoteRole,
  attendancePolicy = null,
} = {}) {
  const team = isTeamCallContext(callContext);
  const agenda = isAgendaCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const voPrivate = isVoSideRoomCallContext(callContext);
  const isAgent = joinRole === "agent";
  const cause = String(endCause || "self");

  if (
    cause === "missed" ||
    cause === "no_agent" ||
    cause === "missedNoAgent" ||
    cause === "missed_no_agent"
  ) {
    if (virtualOffice) {
      return {
        title: "Ninguém entrou a tempo",
        body: "Volte pelo Escritório Virtual no cockpit quando quiser retomar a presença.",
      };
    }
    if (voPrivate) {
      return {
        title: "A conversa privada não foi atendida",
        body: "Volte ao escritório virtual e tente chamar o colega novamente.",
      };
    }
    if (team) {
      return {
        title: "A chamada não foi atendida",
        body: "Ninguém entrou a tempo. Peça um novo convite no chat interno se ainda precisarem.",
      };
    }
    if (isAgent) {
      return {
        title: "Chamada perdida",
        body: "O cliente aguardou sem atendimento. Se precisar continuar, abra um novo convite pela solicitação.",
      };
    }
    return {
      title: "Não foi possível atender",
      body: "Não conseguimos atender agora — continue pelo chat",
    };
  }

  if (cause === "already_ended") {
    if (virtualOffice) {
      return {
        title: "Sala do escritório indisponível",
        body: "A sala não está ativa no momento. Entre de novo pelo Escritório Virtual no cockpit.",
      };
    }
    if (team) {
      return {
        title: "Esta chamada foi encerrada",
        body: "A sala da equipe não está mais ativa. Peça um novo convite no chat interno se ainda precisarem conversar.",
      };
    }
    if (agenda) {
      return {
        title: "Esta reunião foi encerrada",
        body: "A sala deste compromisso não está mais ativa. Abra um novo compromisso na agenda se ainda precisarem se reunir.",
      };
    }
    return {
      title: "Esta chamada foi encerrada",
      body: isAgent
        ? "A sala não está mais ativa. Se precisar continuar, abra um novo convite pela solicitação."
        : fromLobbyEntry
          ? "A sala não está mais ativa. Para um novo atendimento, use o link público do canal."
          : "A sala não está mais ativa. Peça um novo link à equipe se ainda precisar de atendimento.",
    };
  }

  if (cause === "remote") {
    if (virtualOffice) {
      return {
        title: "Você foi desconectado",
        body: virtualOfficeCopy(attendancePolicy).disconnectedBody,
      };
    }
    if (team) {
      return {
        title: "A chamada foi encerrada",
        body: "Alguém encerrou a sala para todos. Você pode entrar de novo pelo convite no chat interno se ainda precisarem.",
      };
    }
    if (agenda) {
      return {
        title: "A reunião foi encerrada",
        body: "A sala foi encerrada para todos. Se o compromisso ainda estiver no horário, entre de novo pelo link da agenda.",
      };
    }
    if (isAgent) {
      const clientLeft = !remoteRole || remoteRole === "guest";
      return {
        title: clientLeft ? "O cliente encerrou a chamada" : "A chamada foi encerrada",
        body: "A sala não está mais ativa. Se precisar continuar, abra um novo convite pela solicitação.",
      };
    }
    const agentLeft = !remoteRole || remoteRole === "agent";
    return {
      title: agentLeft ? "O atendente encerrou a chamada" : "A chamada foi encerrada",
      body: fromLobbyEntry
        ? "A equipe encerrou a chamada. Para um novo atendimento, use o link público do canal."
        : "A equipe encerrou a chamada. Pode fechar esta aba.",
    };
  }

  if (cause === "ended_for_all") {
    if (virtualOffice) {
      return {
        title: "Você saiu do escritório virtual",
        body: virtualOfficeCopy(attendancePolicy).endedBody,
      };
    }
    if (team) {
      return {
        title: "Você encerrou a chamada",
        body: "A sala da equipe foi encerrada para todos.",
      };
    }
    if (agenda) {
      return {
        title: "Você encerrou a reunião",
        body: "A sala foi encerrada para todos os participantes deste compromisso.",
      };
    }
    return {
      title: "Você encerrou a chamada",
      body: isAgent
        ? "A sala foi encerrada para o cliente e para a equipe."
        : "A chamada foi encerrada.",
    };
  }

  // self leave — sala pode continuar ativa
  if (virtualOffice) {
    return {
      title: "Você saiu do escritório virtual",
      body: virtualOfficeCopy(attendancePolicy).endedBody,
    };
  }
  if (voPrivate) {
    return {
      title: "Você saiu da conversa privada",
      body: "O escritório virtual continua aberto. Volte pelo cockpit ou pela aba do floor se precisar.",
    };
  }
  if (team) {
    return {
      title: "Você saiu da chamada",
      body: "A sala da equipe continua aberta para quem ficou. Reabra pelo convite no chat interno se quiser voltar.",
    };
  }
  if (agenda) {
    return {
      title: "Você saiu da reunião",
      body: "A sala continua aberta para quem ficou. Entre de novo pelo link do compromisso se quiser voltar.",
    };
  }

  if (isAgent) {
    return {
      title: "Você saiu do atendimento",
      body: "A sala continua ativa se o cliente ainda estiver nela. Entre de novo pela solicitação para reassumir.",
    };
  }
  return {
    title: "Você saiu da chamada",
    body: fromLobbyEntry
      ? "Se a sala ainda estiver aberta, você pode entrar de novo. Para um novo atendimento, use o link público do canal."
      : "Obrigado por participar. Se a sala ainda estiver aberta, você pode entrar novamente.",
  };
}

/**
 * Classifica erro de entrada na chamada com título, corpo, ação e CTA.
 * Usado na tela de erro para orientar o usuário (e o suporte) sem mensagem genérica.
 */
export function classifyJoinVideoError(
  raw,
  {
    code = "",
    action = "",
    missingCompany = false,
    role = "guest",
    callContext = CALL_CONTEXT_SUPPORT,
  } = {}
) {
  const team = isTeamCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const isAgent = role === "agent";
  const errCode = String(code || "").trim();
  const msg = String(raw || "").trim();
  const nextStep = String(action || "").trim();

  const withSupport = (guide) => ({
    ...guide,
    supportCode: errCode || guide.supportCode || null,
    action: guide.action || nextStep || null,
  });

  if (missingCompany) {
    return withSupport({
      title: "Link incompleto",
      body: virtualOffice
        ? "Falta o workspace neste link. Abra o escritório virtual novamente pelo cockpit."
        : team
          ? "Falta o workspace neste link. Abra a chamada novamente pelo chat da equipe."
          : isAgent
            ? "Falta o workspace neste link. Abra a chamada novamente pela solicitação no XPro."
            : "Este link está incompleto. Peça um novo link à equipe de atendimento.",
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: "video.link_incomplete",
    });
  }

  if (
    errCode === "video.session_inactive" ||
    errCode === "errors.auth.user_inactive" ||
    /usuário inválido ou inativo/i.test(msg)
  ) {
    return withSupport({
      title: "Sessão inválida neste workspace",
      body: team || virtualOffice
        ? "Sua conta não está ativa aqui, ou esta aba abriu com sessão de outro ambiente."
        : "Sua conta não está ativa neste workspace, ou a sessão pertence a outro ambiente.",
      action:
        nextStep ||
        (virtualOffice
          ? "Feche esta aba, confirme o workspace no XPro e entre de novo pelo Escritório Virtual."
          : team
            ? "Feche esta aba, confirme o workspace no XPro e abra a chamada pelo chat da equipe."
            : "Feche esta aba, entre no workspace certo no XPro e abra a chamada pela solicitação."),
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar e voltar ao XPro",
      supportCode: errCode || "video.session_inactive",
    });
  }

  if (errCode === "video.session_required" || errCode === "errors.auth.token_missing") {
    return withSupport({
      title: "É preciso estar logado",
      body: virtualOffice
        ? "Para entrar no escritório virtual, abra a sala pelo Escritório Virtual no XPro com sua sessão ativa."
        : team
          ? "Para entrar na chamada da equipe, abra o link pelo chat interno do XPro com sua sessão ativa."
          : "Para entrar como atendente, abra a chamada pelo XPro com sua sessão ativa.",
      action:
        nextStep ||
        (virtualOffice
          ? "Volte ao Escritório Virtual no cockpit e ingresse novamente."
          : team
            ? "Volte ao chat da equipe e toque em Entrar / Abrir chamada."
            : "Volte à solicitação no XPro e abra a chamada novamente."),
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: errCode || "video.session_required",
    });
  }

  if (errCode === "video.session_revoked" || errCode === "errors.auth.session_revoked") {
    return withSupport({
      title: "Sessão encerrada",
      body: "Sua sessão foi encerrada. Entre novamente no XPro e abra a chamada de novo.",
      action:
        nextStep ||
        (virtualOffice
          ? "Faça login no workspace correto e abra o escritório virtual pelo cockpit."
          : team
            ? "Faça login no workspace correto e abra a chamada pelo chat da equipe."
            : "Faça login novamente e abra a chamada pela solicitação."),
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: errCode || "video.session_revoked",
    });
  }

  if (
    errCode === "video.session_workspace_mismatch" ||
    errCode === "video.session_invalid" ||
    errCode === "errors.auth.token_invalid" ||
    errCode === "errors.auth.partner_no_tenant_user" ||
    errCode === "errors.auth.platform_no_tenant_user" ||
    errCode === "errors.auth.partner_workspace_denied"
  ) {
    return withSupport({
      title: "Sem acesso a este workspace",
      body: virtualOffice
        ? "Não foi possível validar sua sessão neste workspace para o escritório virtual."
        : team
          ? "Não foi possível validar sua sessão neste workspace para a chamada da equipe."
          : "Não foi possível validar sua sessão para entrar na chamada.",
      action:
        nextStep ||
        (virtualOffice
          ? "Entre no workspace correto no XPro e abra o escritório virtual pelo cockpit."
          : team
            ? "Entre no workspace correto no XPro e abra a chamada pelo chat da equipe."
            : "Entre no workspace correto no XPro e abra a chamada pela solicitação."),
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: errCode || "video.session_invalid",
    });
  }

  if (errCode === "video.private_forbidden") {
    return withSupport({
      title: "Chamada privada",
      body: "Você não tem permissão para entrar nesta chamada privada.",
      action: team
        ? "Peça para quem iniciou a chamada tornar a sala aberta ou te incluir."
        : "Peça acesso a quem iniciou a chamada.",
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: "video.private_forbidden",
    });
  }

  if (
    errCode === "video.camera_required" ||
    /camera_required|exige câmera|exige camera|sem vídeo neste dispositivo|sem video neste dispositivo/i.test(
      msg
    )
  ) {
    return withSupport({
      title: "Câmera obrigatória",
      body: "Esta chamada exige câmera ligada. Sem câmera neste dispositivo, não é possível entrar.",
      canRetry: true,
      primaryAction: "retry",
      primaryLabel: "Tentar novamente",
      supportCode: errCode || "video.camera_required",
    });
  }

  if (/company/i.test(msg) && /obrigat|required|incomplete/i.test(msg)) {
    return withSupport({
      title: "Link incompleto",
      body: team
        ? "Falta o workspace neste link. Abra a chamada novamente pelo chat da equipe."
        : isAgent
          ? "Falta o workspace neste link. Abra a chamada novamente pela solicitação no XPro."
          : "Este link está incompleto. Peça um novo link à equipe de atendimento.",
      canRetry: false,
      primaryAction: "reload",
      primaryLabel: "Recarregar página",
      supportCode: "video.link_incomplete",
    });
  }

  if (
    errCode === "video.twilio_config_missing" ||
    /twilio_config|credenciais|não está configurad/i.test(msg)
  ) {
    return withSupport({
      title: "Vídeo não configurado",
      body: team || isAgent
        ? "O canal de Chamadas de Vídeo ainda não está configurado. Verifique em Configurações → Canais."
        : "A chamada de vídeo ainda não está configurada. Avise a equipe de atendimento.",
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: errCode || "video.twilio_config_missing",
    });
  }

  if (
    errCode === "video.schema_missing" ||
    /schema_missing|ainda está sendo preparado|ainda está sendo preparada/i.test(msg)
  ) {
    return withSupport({
      title: "Preparando vídeo",
      body: team || isAgent
        ? "O recurso de vídeo ainda está sendo preparado neste workspace. Aguarde alguns segundos e tente de novo."
        : "Estamos preparando a sala. Aguarde alguns segundos e tente de novo.",
      canRetry: true,
      primaryAction: "retry",
      primaryLabel: "Tentar novamente",
      supportCode: errCode || "video.schema_missing",
    });
  }

  if (/recording_consent|concordar com a gravação|aceite da gravação/i.test(msg)) {
    return withSupport({
      title: "Aceite da gravação",
      body: "Para entrar nesta chamada, marque o aceite da gravação (LGPD).",
      canRetry: true,
      primaryAction: "retry",
      primaryLabel: "Voltar e aceitar",
      supportCode: "video.recording_consent_required",
    });
  }

  if (/não encontrad|indispon|404|410|encerrad/i.test(msg) || errCode === "video.room_ended") {
    return withSupport({
      title: "Sala indisponível",
      body: team
        ? "Esta sala da equipe não está mais disponível. Peça um novo convite no chat interno."
        : isAgent
          ? "Esta sala não está mais disponível. Se o cliente ainda estiver aguardando, peça que ele entre de novo pelo lobby ou reenvie o convite."
          : "Esta sala não está disponível. Confira o link ou peça um novo à equipe.",
      canRetry: false,
      primaryAction: "close",
      primaryLabel: "Fechar",
      supportCode: errCode || "video.room_unavailable",
    });
  }

  if (
    errCode === "video.public_lookup_failed" ||
    errCode === "video.lookup_failed" ||
    errCode === "video.temporary_failure" ||
    /Column |Table |ER_|cannot be null|doesn't exist|SQL|ECONNREFUSED|undefined is not|Network Error/i.test(
      msg
    )
  ) {
    return withSupport({
      title: "Falha temporária",
      body: team || isAgent
        ? "Não foi possível preparar a chamada agora. Tente novamente em instantes ou verifique o status do canal de vídeo."
        : "Não foi possível preparar a chamada agora. Tente novamente em instantes.",
      canRetry: true,
      primaryAction: "retry",
      primaryLabel: "Tentar novamente",
      supportCode: errCode || "video.temporary_failure",
    });
  }

  const fallbackBody = !msg
    ? team
      ? "Não foi possível entrar na chamada da equipe. Tente novamente pelo chat interno."
      : isAgent
        ? "Não foi possível entrar na chamada. Tente novamente pela solicitação."
        : "Não foi possível entrar na chamada. Tente novamente."
    : msg.length > 180
      ? team
        ? "Algo deu errado ao entrar na chamada da equipe. Tente novamente pelo chat interno."
        : isAgent
          ? "Algo deu errado ao entrar na chamada. Tente novamente pela solicitação."
          : "Algo deu errado ao entrar na chamada. Tente novamente."
      : msg;

  return withSupport({
    title: team || isAgent ? "Não foi possível entrar na chamada" : "Não foi possível entrar",
    body: fallbackBody,
    canRetry: true,
    primaryAction: "retry",
    primaryLabel: "Tentar novamente",
    supportCode: errCode || null,
  });
}

/** Mensagens de erro amigáveis por contexto (atendimento vs equipe). */
export function friendlyVideoError(
  raw,
  { code = "", action = "", missingCompany = false, role = "guest", callContext = CALL_CONTEXT_SUPPORT } = {}
) {
  const guide = classifyJoinVideoError(raw, { code, action, missingCompany, role, callContext });
  if (guide.action && guide.action !== guide.body) {
    return `${guide.body} ${guide.action}`.trim();
  }
  return guide.body;
}

export function connectingCopy({ callContext, joinRole, phase } = {}) {
  const team = isTeamCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const voPrivate = isVoSideRoomCallContext(callContext);
  const isAgent = joinRole === "agent";
  if (phase === "connecting") {
    if (voPrivate) {
      return {
        title: "Entrando na conversa privada…",
        body: "Conectando você à sala. O escritório virtual permanece aberto.",
      };
    }
    if (virtualOffice) {
      return {
        title: "Entrando no escritório virtual…",
        body: "Conectando você à sala da equipe. Isso leva só alguns segundos.",
      };
    }
    if (team) {
      return {
        title: "Entrando na chamada da equipe…",
        body: "Conectando você à sala interna. Isso leva só alguns segundos.",
      };
    }
    return {
      title: isAgent ? "Entrando para atender…" : "Entrando na chamada…",
      body: isAgent
        ? "Conectando você à sala do cliente."
        : "Conectando você à sala. Isso leva só alguns segundos.",
    };
  }
  if (virtualOffice) {
    return {
      title: "Carregando…",
      body: "Preparando o escritório virtual.",
    };
  }
  if (team) {
    return {
      title: "Carregando…",
      body: "Preparando a sala da equipe.",
    };
  }
  return {
    title: "Carregando…",
    body: isAgent ? "Preparando a sala de atendimento." : "Preparando sua sala de vídeo.",
  };
}

export function errorScreenCopy({ callContext, joinRole } = {}) {
  const team = isTeamCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const isAgent = joinRole === "agent";
  if (virtualOffice) {
    return {
      title: "Não foi possível entrar no escritório",
      fallbackBody: "Tente novamente pelo Escritório Virtual no cockpit.",
    };
  }
  if (team) {
    return {
      title: "Não foi possível entrar na chamada",
      fallbackBody: "Tente novamente pelo chat da equipe.",
    };
  }
  return {
    title: isAgent ? "Não foi possível entrar na chamada" : "Não foi possível entrar",
    fallbackBody: isAgent
      ? "Tente novamente pela solicitação no XPro."
      : "Tente novamente em instantes.",
  };
}

export function preJoinCopy({
  callContext,
  joinRole,
  fromLobbyEntry,
  isLobbyGuestForm,
  roomLabel,
  requireCamera = false,
} = {}) {
  const team = isTeamCallContext(callContext);
  const agenda = isAgendaCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const isAgent = joinRole === "agent";
  const requiredFooter = "A câmera permanece ligada durante toda a chamada.";
  const requiredSubtitle = "É preciso entrar com a câmera ligada.";

  if (virtualOffice) {
    return {
      title: "Escritório Virtual",
      subtitle: "Confirme seu nome e entre para marcar presença com a equipe.",
      nameLabel: "Seu nome",
      namePlaceholder: "Como a equipe verá seu nome",
      joinButton: "Ingressar no escritório",
      footer: "Câmera e microfone são opcionais — use os controles ao lado.",
      shareLabel: null,
      showShare: false,
      rejoinButton: "Voltar ao escritório",
    };
  }

  if (isVoPrivateCallContext(callContext) || isVoHuddleCallContext(callContext)) {
    return {
      title: isVoHuddleCallContext(callContext) ? "Sala do escritório" : "Conversa privada",
      subtitle: "Conversa iniciada a partir do escritório virtual.",
      nameLabel: "Seu nome",
      namePlaceholder: "Como a equipe verá seu nome",
      joinButton: "Entrar na conversa",
      footer: "Câmera e microfone são opcionais. O piso do escritório permanece aberto.",
      shareLabel: null,
      showShare: false,
      rejoinButton: "Voltar à conversa",
    };
  }

  if (team) {
    return {
      title: "Chamada da equipe",
      subtitle: requireCamera
        ? requiredSubtitle
        : "Confirme seu nome e entre para falar com os colegas.",
      nameLabel: "Seu nome",
      namePlaceholder: "Como a equipe verá seu nome",
      joinButton: requireCamera ? "Entrar com a câmera" : "Entrar na chamada",
      footer: requireCamera
        ? requiredFooter
        : "Câmera e microfone seguem os controles ao lado.",
      shareLabel: "Convidar colegas",
      showShare: true,
      rejoinButton: "Entrar de novo na sala",
    };
  }

  if (agenda) {
    return {
      title: "Reunião",
      subtitle: requireCamera
        ? requiredSubtitle
        : "Confirme seu nome e entre na sala deste compromisso.",
      nameLabel: "Seu nome",
      namePlaceholder: "Como os participantes verão seu nome",
      joinButton: requireCamera ? "Entrar com a câmera" : "Entrar na reunião",
      footer: requireCamera
        ? requiredFooter
        : "Câmera e microfone seguem os controles ao lado.",
      shareLabel: "Convidar participantes",
      showShare: true,
      rejoinButton: "Entrar de novo na reunião",
    };
  }

  if (fromLobbyEntry && !isAgent) {
    return {
      title: "Aguarde um atendente",
      subtitle: isLobbyGuestForm
        ? "Confirme seus dados para entrar na fila."
        : "Confirme seu nome para participar.",
      nameLabel: "Seu nome",
      namePlaceholder: "Como devemos te chamar?",
      joinButton: "Participar agora",
      footer: "Câmera e microfone seguem os controles ao lado.",
      shareLabel: null,
      showShare: false,
      rejoinButton: "Entrar de novo nesta sala",
    };
  }

  if (isAgent) {
    return {
      title: "Pronto para atender",
      subtitle: requireCamera
        ? requiredSubtitle
        : "Confirme seu nome e entre para atender o cliente.",
      nameLabel: "Seu nome (atendente)",
      namePlaceholder: "Como o cliente verá seu nome",
      joinButton: requireCamera ? "Entrar com a câmera" : "Entrar para atender",
      footer: requireCamera
        ? requiredFooter
        : "Câmera e microfone seguem os controles ao lado.",
      shareLabel: "Link para o cliente",
      showShare: true,
      rejoinButton: "Entrar de novo na sala",
    };
  }

  return {
    title: "Sua chamada está pronta",
    subtitle: requireCamera
      ? requiredSubtitle
      : isLobbyGuestForm
        ? "Confirme seus dados para entrar na fila."
        : "Confirme seu nome para participar.",
    nameLabel: "Seu nome",
    namePlaceholder: "Como devemos te chamar?",
    joinButton: requireCamera ? "Entrar com a câmera" : "Participar agora",
    footer: requireCamera
      ? requiredFooter
      : "Câmera e microfone seguem os controles ao lado.",
    shareLabel: "Convidar alguém",
    showShare: !isLobbyGuestForm,
    rejoinButton: "Entrar de novo nesta sala",
  };
}

export function crowdCapacityCopy({ callContext } = {}) {
  const team = isTeamCallContext(callContext);
  return {
    banner: team
      ? "Muitos participantes — priorizamos quem está falando."
      : "Muitos participantes — priorizamos quem está falando.",
  };
}

export function waitingInCallCopy({ callContext, joinRole, fromLobbyEntry, attendancePolicy = null } = {}) {
  const team = isTeamCallContext(callContext);
  const agenda = isAgendaCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const isAgent = joinRole === "agent";

  if (virtualOffice) {
    const vo = virtualOfficeCopy(attendancePolicy);
    return {
      title: vo.inCallTitle,
      body: vo.inCallBody,
      showCopyLink: false,
    };
  }

  if (team) {
    return {
      title: "Aguardando colegas",
      body: "Você já está na sala. Outros participantes podem entrar pelo convite no chat interno.",
      showCopyLink: true,
    };
  }

  if (agenda) {
    return {
      title: "Aguardando participantes",
      body: "Você já está na sala. Os outros entram pelo link deste compromisso.",
      showCopyLink: true,
    };
  }

  if (fromLobbyEntry && !isAgent) {
    return {
      title: "Aguardando atendimento",
      body: "Você já está na sala. A equipe foi avisada e um atendente entrará em breve.",
      showCopyLink: false,
    };
  }

  if (isAgent) {
    return {
      title: "Aguardando o cliente",
      body: "Você já está na sala. O cliente entrará pelo link enviado ou pelo lobby.",
      showCopyLink: true,
    };
  }

  return {
    title: "Aguardando a equipe",
    body: "Você já está na sala. A equipe foi avisada — um atendente entrará em breve. Enquanto isso, você pode compartilhar o link se precisar.",
    showCopyLink: true,
  };
}

/**
 * Copy do aceite de gravação (LGPD) no pré-join.
 */
export function recordingConsentCopy({ callContext, recordingVisibility } = {}) {
  const team = isTeamCallContext(callContext);
  const virtualOffice = isVirtualOfficeCallContext(callContext);
  const visibility = String(recordingVisibility || "space").toLowerCase();
  const visibilityHint = virtualOffice
    ? visibility === "creator"
      ? "A gravação fica visível ao administrador da sala."
      : "A gravação fica visível a quem tem acesso a este escritório virtual."
    : team
      ? visibility === "creator"
        ? "A gravação fica visível ao criador da chamada."
        : "A gravação fica visível a quem tem acesso a este espaço."
      : "A gravação fica visível à equipe de atendimento desta solicitação.";
  return {
    checkboxLabel: virtualOffice
      ? "Concordo com a gravação desta sala"
      : "Concordo com a gravação desta chamada",
    helper: `${visibilityHint} Uso conforme a LGPD.`,
    details:
      "A gravação e a transcrição podem servir para registro, qualidade e eventual comprovação (Lei nº 13.709/2018). Ao marcar, você autoriza o tratamento desses dados. Solicite acesso, correção ou exclusão pelos canais de privacidade da empresa.",
    detailsToggle: "Saiba mais sobre a gravação",
    requiredHint: "Marque o aceite da gravação para continuar.",
  };
}

/** Copy do estado câmera/microfone desligados (pré-join, tile e PIP). */
export function cameraOffPresenceCopy({
  self = false,
  name = "",
  muted = false,
  compact = false,
  virtualOffice = false,
  status = "present",
  cargo = "",
} = {}) {
  if (virtualOffice) {
    return floorPresenceCopy({ self, name, status, compact, cargo });
  }
  const who = String(name || "").trim() || "Esta pessoa";
  if (self) {
    if (compact) {
      return {
        title: "Câmera desligada",
        body: muted ? "Câmera e microfone desligados" : "Ninguém te vê agora",
      };
    }
    if (muted) {
      return {
        title: "Sua câmera e o microfone estão desligados",
        body: "Ninguém te vê nem te ouve. Use os controles da chamada para ligar quando quiser aparecer.",
      };
    }
    return {
      title: "Sua câmera está desligada",
      body: "Os outros participantes não veem você. Ligue a câmera para aparecer na chamada.",
    };
  }
  if (compact) {
    return {
      title: who,
      body: muted ? "Câmera e microfone desligados" : "Câmera desligada",
    };
  }
  return {
    title: who,
    body: muted
      ? "Está com a câmera e o microfone desligados"
      : "Está com a câmera desligada",
  };
}

export function micOffPresenceLabel({ self = false } = {}) {
  return self ? "Seu microfone está desligado" : "Microfone desligado";
}

export function mediaControlCopy({
  camOff = false,
  muted = false,
  requireCamera = false,
  teamJoinMutedHint = false,
  virtualOffice = false,
  speakerMuted = false,
} = {}) {
  return {
    micTitle: muted
      ? teamJoinMutedHint
        ? "Microfone desligado ao entrar — clique para falar"
        : virtualOffice
          ? "Microfone desligado — você está no escritório em silêncio"
          : "Microfone desligado — clique para ligar. Ninguém te ouve agora."
      : virtualOffice
        ? "Silenciar microfone no escritório"
        : "Desligar microfone",
    camTitle: requireCamera
      ? "Câmera obrigatória nesta chamada — não é possível desligar o vídeo."
      : camOff
        ? virtualOffice
          ? "Câmera desligada — opcional no escritório virtual"
          : "Câmera desligada — clique para ligar. Ninguém te vê agora."
        : virtualOffice
          ? "Desligar câmera (opcional no escritório)"
          : "Desligar câmera",
    speakerTitle: speakerMuted
      ? "Áudio desligado — clique para ouvir quem está falando"
      : "Silenciar o áudio do escritório — você deixa de ouvir quem está falando",
  };
}
